// src/pages/AgenticAIPage.js
import React, { useEffect, useRef } from 'react';
import { InfraDemo } from '../components/InfraDemo/InfraDemo';
import './AgenticAIPage.css';

export function AgenticAIPage() {
  const pageRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.15 }
    );

    const reveals = pageRef.current?.querySelectorAll('.reveal');
    reveals?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="agentic-ai-page" ref={pageRef}>
      <div className="container">
        <header className="page-header reveal">
          <p className="page-label">Understanding AI Agents</p>
          <h1>Agentic AI</h1>
          <p className="page-subtitle">How autonomous AI systems reason, act, and take actions on your behalf</p>
        </header>

        {/* 1. What is an Agent */}
        <section className="agentic-section reveal">
          <div className="section-header">
            <span className="section-label">Definition</span>
            <div className="section-rule"></div>
          </div>
          <h2>What is an AI Agent?</h2>
          <p className="lead-text">
            This question furrows a lot of brows. The simplest answer:
          </p>
          <blockquote className="definition">
            An AI Agent is a model that can take autonomous actions.
          </blockquote>
          <p>But even that answer isn't so simple, so let's break it down:</p>
        </section>

        {/* 2. Interactive Widget */}
        <section className="agentic-section reveal">
          <div className="section-header">
            <span className="section-label">See It In Action</span>
            <div className="section-rule"></div>
          </div>
          <h2>How Different AI Systems Actually Talk</h2>
          <p className="lead-text">
            Pick a system type, send the preset message, and watch where the request actually travels — and, more importantly, who decides what happens next.
          </p>
          <InfraDemo />
        </section>

        {/* 3. Autonomy */}
        <section className="agentic-section reveal">
          <div className="section-header">
            <span className="section-label">Key Concept</span>
            <div className="section-rule"></div>
          </div>
          <h2>The Defining Feature: Autonomy</h2>
          <p className="highlight-text">
            If the AI is not calling a tool, running code, or triggering workflows, it's not an agent — it's an assistant or a chatbot.
          </p>
        </section>

        {/* 4. Advanced Concepts */}
        <section className="agentic-section reveal">
          <div className="section-header">
            <span className="section-label">Going Deeper</span>
            <div className="section-rule"></div>
          </div>
          <h2>A Deeper Look: The Agent's "Thinking" Process</h2>
          <p>
            The simple workflow above is the foundation. But more advanced agents don't just follow a straight line. They operate in a continuous loop of planning and reasoning.
          </p>

          <p>
            In this model, the AI doesn't just act once. It observes the result of its action and decides if it's achieved the user's goal. If not, it plans its next step and acts again. This loop continues until the task is complete.
          </p>

          <div className="react-info">
            <h3>The ReAct Framework</h3>
            <p>
              This iterative approach is known as <strong>ReAct</strong> (Reasoning + Acting). Introduced in a 2023 paper by researchers at Princeton and Google, ReAct combines the reasoning capabilities of language models with the ability to take actions in an environment. The agent alternates between thinking about what to do next (reasoning) and actually doing it (acting), creating a more robust and flexible problem-solving approach.
            </p>
            <p className="resources-links">
              <strong>Learn more:</strong>{' '}
              <a href="https://arxiv.org/abs/2210.03629" target="_blank" rel="noopener noreferrer">
                Original ReAct Paper (arXiv)
              </a>
              {' | '}
              <a href="https://www.ibm.com/think/topics/react-agent" target="_blank" rel="noopener noreferrer">
                IBM's Guide to ReAct Agents
              </a>
            </p>
          </div>
        </section>

        {/* 5. Getting Started */}
        <section className="agentic-section reveal">
          <div className="section-header">
            <span className="section-label">Getting Started</span>
            <div className="section-rule"></div>
          </div>
          <h2>How Do I Get Started?</h2>
          <p className="lead-text">
            There are multiple ways to build AI agents, depending on your technical background and needs:
          </p>

          <div className="approach-grid">
            <div className="approach-card">
              <div className="approach-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <polyline points="16 18 22 12 16 6" />
                  <polyline points="8 6 2 12 8 18" />
                </svg>
              </div>
              <h3>Manual Coding</h3>
              <p>
                Build agents from scratch using programming languages like Python. This gives you complete control and flexibility to integrate with any system or API.
              </p>
              <div className="approach-when">
                <strong>Best for:</strong> Custom requirements, complex integrations, full control over agent behavior
              </div>
            </div>

            <div className="approach-card">
              <div className="approach-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="2" y="3" width="20" height="14" rx="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              </div>
              <h3>Agent Harnesses</h3>
              <p>
                Use AI-powered coding tools to help you build agents faster. These tools can generate code, explain implementations, and help debug.
              </p>
              <ul className="tools-list-inline">
                <li>
                  <a href="https://www.claude.com/product/claude-code" target="_blank" rel="noopener noreferrer">
                    <strong>Claude Code</strong>
                  </a> — AI pair programmer
                </li>
                <li>
                  <a href="https://openai.com/codex/" target="_blank" rel="noopener noreferrer">
                    <strong>Codex</strong>
                  </a> — OpenAI's code generation model
                </li>
                <li>
                  <a href="https://opencode.ai/" target="_blank" rel="noopener noreferrer">
                    <strong>OpenCode</strong>
                  </a> — Open-source AI coding agent
                </li>
                <li>
                  <a href="https://pi.dev/" target="_blank" rel="noopener noreferrer">
                    <strong>Pi.dev</strong>
                  </a> — AI coding agent
                </li>
              </ul>
              <div className="approach-when">
                <strong>Best for:</strong> Developers who want to code faster with AI assistance
              </div>
            </div>

            <div className="approach-card">
              <div className="approach-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
              </div>
              <h3>No-Code Platforms</h3>
              <p>
                Build agents using visual interfaces without writing code. Drag-and-drop workflows, pre-built integrations, and templates make it accessible to non-programmers.
              </p>
              <ul className="tools-list-inline">
                <li>
                  <a href="https://flowiseai.com/" target="_blank" rel="noopener noreferrer">
                    <strong>Flowise</strong>
                  </a> — Visual LLM workflow builder
                </li>
                <li>
                  <a href="https://n8n.io/" target="_blank" rel="noopener noreferrer">
                    <strong>n8n</strong>
                  </a> — Workflow automation platform
                </li>
                <li>
                  <a href="https://zapier.com/" target="_blank" rel="noopener noreferrer">
                    <strong>Zapier</strong>
                  </a> — App integration and automation
                </li>
              </ul>
              <div className="approach-when">
                <strong>Best for:</strong> Non-technical users, rapid prototyping, simple workflows
              </div>
            </div>
          </div>

          <div className="tip-block">
            <div className="tip-badge">Tip</div>
            <p>
              Start with a no-code platform to prototype your agent concept quickly. Once you validate the idea and understand the workflow, you can rebuild it with code for more customization and control if needed.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
